import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AdminRequestService, UserRequest, PaginatedResponse } from 'app/services/adminrequest/adminrequest.service';

@Component({
  selector: 'app-admin-requests',
  templateUrl: './admin-requests.component.html',
  styleUrls: ['./admin-requests.component.scss']
})
export class AdminRequestsComponent implements OnInit, OnDestroy {
  // Existing data for pending tab (non‑paginated)
  pendingRequests: UserRequest[] = [];
  
  // Paginated data for "all" tab
  allRequests: UserRequest[] = [];
  totalElements = 0;
  totalPages = 0;
  currentPage = 0;
  pageSize = 10;
  
  // Search & filters
  searchTerm = '';
  filterStatus = '';
  filterType = '';
  
  activeTab: 'pending' | 'all' = 'pending';
  loading = false;
  error = '';
  
  stats: any = null;
  loadingStats = false;
  
  // Reject modal
  showRejectModal = false;
  selectedRequestId: number | null = null;
  rejectComment = '';
  
  // Refresh subscription
  private refreshSubscription: any;

  constructor(
    private adminRequestService: AdminRequestService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadStats();
    this.loadRequests();
    
    this.refreshSubscription = this.adminRequestService.refreshNeeded$.subscribe(() => {
      this.loadStats();
      this.loadRequests();
    });
  }

  ngOnDestroy(): void {
    if (this.refreshSubscription) this.refreshSubscription.unsubscribe();
  }

  loadStats(): void {
    this.loadingStats = true;
    this.adminRequestService.getStats().subscribe({
      next: (data) => { this.stats = data; this.loadingStats = false; },
      error: (err) => { console.error(err); this.loadingStats = false; }
    });
  }

  loadRequests(): void {
    if (this.activeTab === 'pending') {
      this.loadPendingRequests();
    } else {
      this.loadAllRequestsPaginated();
    }
  }

  loadPendingRequests(): void {
    this.loading = true;
    this.error = '';
    this.adminRequestService.getPendingRequests().subscribe({
      next: (data) => { this.pendingRequests = data; this.loading = false; },
      error: (err) => { this.error = err.message; this.loading = false; }
    });
  }

  loadAllRequestsPaginated(): void {
    this.loading = true;
    this.error = '';
    this.adminRequestService.getRequestsPaginated(
      this.currentPage,
      this.pageSize,
      this.searchTerm || undefined,
      this.filterStatus || undefined,
      this.filterType || undefined
    ).subscribe({
      next: (response: PaginatedResponse<UserRequest>) => {
        this.allRequests = response.content;
        this.totalElements = response.totalElements;
        this.totalPages = response.totalPages;
        this.currentPage = response.currentPage;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.message || 'Erreur de chargement';
        this.loading = false;
      }
    });
  }

  setTab(tab: 'pending' | 'all'): void {
    this.activeTab = tab;
    if (tab === 'all') {
      // Reset pagination and filters when switching to "all"
      this.currentPage = 0;
      this.searchTerm = '';
      this.filterStatus = '';
      this.filterType = '';
    }
    this.loadRequests();
  }

  onSearch(): void {
    this.currentPage = 0;
    this.loadAllRequestsPaginated();
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filterStatus = '';
    this.filterType = '';
    this.currentPage = 0;
    this.loadAllRequestsPaginated();
  }

  onPageChange(page: number): void {
    if (page >= 0 && page < this.totalPages) {
      this.currentPage = page;
      this.loadAllRequestsPaginated();
    }
  }

  goToDetail(requestId: number): void {
    this.router.navigate(['/admin-requests', requestId]);
  }

  approveRequest(id: number): void {
    if (confirm('Approuver cette demande ?')) {
      this.adminRequestService.approveRequest(id).subscribe({
        next: () => this.loadRequests()
      });
    }
  }

  openRejectModal(id: number): void {
    this.selectedRequestId = id;
    this.rejectComment = '';
    this.showRejectModal = true;
  }

  confirmReject(): void {
    if (!this.rejectComment.trim()) {
      alert('Veuillez fournir un commentaire pour le rejet.');
      return;
    }
    if (this.selectedRequestId) {
      this.adminRequestService.rejectRequest(this.selectedRequestId, this.rejectComment).subscribe({
        next: () => {
          this.showRejectModal = false;
          this.loadRequests();
        },
        error: (err) => console.error(err)
      });
    }
  }

  deleteRequest(id: number): void {
    if (confirm('Supprimer définitivement cette demande ?')) {
      this.adminRequestService.deleteRequest(id).subscribe({
        next: () => this.loadRequests()
      });
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'APPROUVE': return 'badge-success';
      case 'REFUSE': return 'badge-danger';
      default: return 'badge-warning';
    }
  }

  getOperationLabel(operation: string): string {
    switch(operation) {
      case 'AJOUT': return '➕ Ajout';
      case 'MODIFICATION': return '✏️ Modification';
      case 'SUPPRESSION': return '🗑️ Suppression';
      default: return operation;
    }
  }
}